import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Mail, Users, DollarSign, Zap, CheckCircle, ArrowRight, Briefcase, TrendingUp,
  Shield, Play, MousePointerClick, BarChart3, Sparkles, Network,
  MessageCircle, Calendar, UserPlus, Bot, MapPin, Star, Target, Award,
  Clock, Gavel, Bell, Heart, Send, ChevronDown, XCircle, Trophy, Check,
  Home, Calculator, Palette, Megaphone, Building2, FileText, Hammer, Search, Building
} from 'lucide-react';
import { Logo } from '../components/Logo';
import LandingChatBot from '../components/LandingChatBot';
import ExitIntentSurvey from '../components/ExitIntentSurvey';
import SuccessStoriesSection from '../components/SuccessStoriesSection';
import TutorialVideoModal from '../components/TutorialVideoModal';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { logFunnel, trackDwellTime } from '../lib/funnel';
import { useState, useEffect, useRef } from 'react';

const ROTATING_WORDS = ['Revenue', 'Partners', 'Referrals', 'Growth'];

const PROFESSIONAL_PHOTOS = [
  { url: 'https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=200&h=200&fit=crop&crop=face', name: 'Sarah Johnson', profession: 'Real Estate Agent', company: 'Premier Realty', match: '95%' },
  { url: 'https://images.unsplash.com/photo-1659353221237-6a1cfb73fd90?w=200&h=200&fit=crop&crop=face', name: 'Michael Chen', profession: 'Mortgage Broker', company: 'HomeFirst Finance', match: '92%' },
  { url: 'https://images.unsplash.com/photo-1758598306913-5cd682b9e53b?w=200&h=200&fit=crop&crop=face', name: 'Emily Rodriguez', profession: 'Insurance Agent', company: 'SecureLife Insurance', match: '89%' },
  { url: 'https://images.unsplash.com/photo-1599132880549-bffe2907f808?w=200&h=200&fit=crop&crop=face', name: 'David Kim', profession: 'Financial Advisor', company: 'Wealth Management Co', match: '87%' },
  { url: 'https://images.unsplash.com/photo-1603252112050-5ee77b4b4fde?w=200&h=200&fit=crop&crop=face', name: 'James Wilson', profession: 'Attorney', company: 'Wilson Law Firm', match: '84%' },
  { url: 'https://images.unsplash.com/photo-1769636929266-8057f2c5ed52?w=200&h=200&fit=crop&crop=face', name: 'Lisa Thompson', profession: 'Accountant', company: 'Thompson CPA', match: '81%' },
];

const HOW_IT_WORKS = [
  { step: '1', title: 'Join Free', description: 'Create your account in minutes. The first 1,000 members are automatically granted permanent Founding Member status — free premium for life, no invites required.', icon: UserPlus, color: 'bg-green-500' },
  { step: '2', title: 'Find Your Partners', description: 'AI matches you with complementary pros. A real estate agent meets a mortgage broker — the perfect cross-referral.', icon: Target, color: 'bg-blue-500' },
  { step: '3', title: 'Premium Unlocked Day 1', description: 'As a Founding Member, all 17 signature templates — including the 3 premium animated ones — are unlocked from the moment you sign up.', icon: Award, color: 'bg-amber-500' },
  { step: '4', title: 'Earn & Scale', description: 'Pros advertise on your signature — you earn 50% commission. Pick slots to advertise on theirs. Everyone grows.', icon: DollarSign, color: 'bg-green-600' },
];

// Pro Hero Cards
const ProHeroCards = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % PROFESSIONAL_PHOTOS.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative" data-testid="pro-hero-cards">
      <div className="flex justify-center gap-4 overflow-hidden py-4">
        {PROFESSIONAL_PHOTOS.map((pro, i) => {
          const isActive = i === activeIndex;
          const isPrev = i === (activeIndex - 1 + PROFESSIONAL_PHOTOS.length) % PROFESSIONAL_PHOTOS.length;
          const isNext = i === (activeIndex + 1) % PROFESSIONAL_PHOTOS.length;
          return (
            <div
              key={i}
              className={`transition-all duration-500 ease-out ${
                isActive ? 'scale-110 z-10 opacity-100' :
                isPrev || isNext ? 'scale-90 opacity-60' : 'scale-75 opacity-30 hidden md:block'
              }`}
            >
              <div className="bg-white rounded-xl shadow-lg p-5 w-52 text-center border border-border/50 relative">
                {isActive && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {pro.match} match
                  </div>
                )}
                <img src={pro.url} alt={pro.name} className="w-20 h-20 rounded-full mx-auto object-cover mb-3 border-3 border-primary/20" />
                <p className="font-semibold text-sm">{pro.name}</p>
                <p className="text-xs text-primary font-medium">{pro.profession}</p>
                <p className="text-[10px] text-muted-foreground">{pro.company}</p>
                {isActive && (
                  <div className="mt-3 flex gap-1.5">
                    <button className="flex-1 bg-primary text-white text-[10px] font-medium py-1.5 rounded-lg">Connect</button>
                    <button className="flex-1 border border-primary text-primary text-[10px] font-medium py-1.5 rounded-lg">View</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-center gap-1.5 mt-2">
        {PROFESSIONAL_PHOTOS.map((_, i) => (
          <button key={i} onClick={() => setActiveIndex(i)} className={`w-2 h-2 rounded-full transition-all ${i === activeIndex ? 'bg-primary w-4' : 'bg-muted-foreground/30'}`} />
        ))}
      </div>
    </div>
  );
};

// Email Promo Mockup
const EmailPromoMockup = () => (
  <div className="relative max-w-2xl mx-auto" data-testid="email-mockup">
    <div className="bg-[#f1f3f4] rounded-t-2xl px-4 pt-3 pb-0 shadow-xl border border-gray-200 border-b-0">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <div className="w-3 h-3 rounded-full bg-yellow-400" />
        <div className="w-3 h-3 rounded-full bg-green-400" />
        <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-500 mx-4">mail.google.com</div>
      </div>
    </div>
    <div className="bg-white rounded-b-2xl shadow-xl border border-gray-200 border-t-0 overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
          <Mail className="w-4 h-4 text-white" />
        </div>
        <span className="text-[#202124] font-semibold text-base">Gmail</span>
        <div className="flex-1 bg-[#eaf1fb] rounded-full px-4 py-1.5 text-xs text-gray-500 max-w-xs">Search mail</div>
      </div>
      <div className="p-5">
        <div className="border-b border-gray-100 pb-3 mb-3 space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 w-8">From:</span>
            <span className="text-[#202124] font-medium">Michael Chen</span>
            <span className="text-gray-400 text-xs">&lt;michael@homefirstfinance.com&gt;</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 w-8">To:</span>
            <span className="text-[#202124]">Sarah Johnson</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 w-8">Sub:</span>
            <span className="text-[#202124] font-medium">Following up on the referral opportunity</span>
          </div>
        </div>
        <div className="text-sm text-gray-700 leading-relaxed mb-5 space-y-2">
          <p>Hi Sarah,</p>
          <p>It was great connecting at the networking event last week. I'd love to explore how we can refer clients to each other.</p>
          <p>Best,<br /><span className="font-medium">Michael</span></p>
        </div>
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-start gap-3 mb-3">
            <img src="https://images.unsplash.com/photo-1659353221237-6a1cfb73fd90?w=60&h=60&fit=crop&crop=face" alt="Michael Chen" className="w-12 h-12 rounded-full object-cover border-2 border-gray-100" />
            <div>
              <p className="font-bold text-sm text-[#202124]">Michael Chen</p>
              <p className="text-xs text-blue-600">Mortgage Broker at HomeFirst Finance</p>
              <p className="text-xs text-gray-500">(248) 555-0192 | Troy, MI</p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden border-2 border-blue-100 shadow-sm">
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5f8a] px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-5 h-5 rounded bg-white p-0.5" />
                <span className="text-white text-xs font-semibold">Recommended by Michael</span>
              </div>
              <span className="text-white/60 text-[10px]">ConnectMail Partner</span>
            </div>
            <div className="bg-gradient-to-br from-white to-blue-50 p-4 flex items-center gap-4">
              <img src="https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=60&h=60&fit=crop&crop=face" alt="Sarah Johnson" className="w-14 h-14 rounded-full object-cover border-2 border-blue-200 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#202124]">Sarah Johnson</p>
                <p className="text-xs text-blue-600 font-medium">Real Estate Agent at Premier Realty</p>
                <div className="flex items-center gap-1 mt-1">
                  {[1,2,3,4,5].map(i => (<Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />))}
                  <span className="text-[10px] text-gray-500 ml-1">4.9 | 86 reviews</span>
                </div>
              </div>
              <button className="bg-[#1e3a5f] text-white text-xs px-4 py-1.5 rounded-lg font-medium flex-shrink-0">View Profile</button>
            </div>
            <div className="bg-green-50 border-t border-green-100 px-4 py-1.5 flex items-center gap-2">
              <DollarSign className="w-3 h-3 text-green-600" />
              <span className="text-[11px] text-green-700 font-medium">Michael earns 50% every time someone clicks this ad</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 animate-bounce">
      <DollarSign className="w-3.5 h-3.5" />
      Earn 50% per click
    </div>
  </div>
);

// AI Coach Preview
const AICoachPreview = () => {
  const [message, setMessage] = useState(0);
  const messages = [
    { type: 'coach', text: "Hi! I'm your AI Networking Coach. Let me help you grow your network." },
    { type: 'coach', text: "Based on your profile, I found 5 mortgage brokers in your area who could be great partners." },
    { type: 'user', text: "That's great! Can you help me write an intro message?" },
    { type: 'coach', text: "Of course! Here's a personalized icebreaker:\n\n\"Hi Sarah, I noticed we both work in the Troy real estate market. I'd love to explore referral opportunities...\"" }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setMessage(prev => (prev + 1) % messages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 shadow-2xl" data-testid="ai-coach-preview">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-700">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-white font-semibold">AI Networking Coach</p>
          <p className="text-slate-400 text-xs">Powered by GPT</p>
        </div>
      </div>
      <div className="space-y-3 min-h-[180px]">
        {messages.slice(0, message + 1).map((msg, i) => (
          <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${msg.type === 'user' ? 'bg-primary text-white rounded-br-sm' : 'bg-slate-700 text-slate-100 rounded-bl-sm'}`}>
              <p className="whitespace-pre-wrap">{msg.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Social Feed Preview
const SocialFeedPreview = () => {
  const MOCK_POSTS = [
    { author: 'Sarah Johnson', role: 'Real Estate Agent', photo: 'https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=60&h=60&fit=crop&crop=face', content: 'Just closed on a beautiful 4-bed home in Troy! Huge thanks to Michael Chen for the seamless mortgage process. This is what great partnerships look like!', likes: 24, comments: 8, time: '2h ago', type: 'update' },
    { author: 'Michael Chen', role: 'Mortgage Broker', photo: 'https://images.unsplash.com/photo-1659353221237-6a1cfb73fd90?w=60&h=60&fit=crop&crop=face', content: 'Pro tip: First-time buyers — get pre-approved BEFORE you start house hunting. It gives you a massive edge in competitive markets. DM me for a free consultation!', likes: 41, comments: 15, time: '5h ago', type: 'tip' },
    { author: 'Emily Rodriguez', role: 'Insurance Agent', photo: 'https://images.unsplash.com/photo-1758598306913-5cd682b9e53b?w=60&h=60&fit=crop&crop=face', content: 'Hosting a networking event next Thursday at the Troy Community Center! All local business owners welcome. Let\'s build some referral partnerships!', likes: 18, comments: 6, time: '1d ago', type: 'event' },
  ];

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="social-feed-preview">
      {MOCK_POSTS.map((post, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <img src={post.photo} alt={post.author} className="w-10 h-10 rounded-full object-cover" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{post.author}</p>
                <p className="text-[11px] text-gray-500">{post.role} | {post.time}</p>
              </div>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                post.type === 'tip' ? 'bg-purple-100 text-purple-700' :
                post.type === 'event' ? 'bg-amber-100 text-amber-700' :
                'bg-blue-100 text-blue-700'
              }`}>{post.type === 'tip' ? 'Pro Tip' : post.type === 'event' ? 'Event' : 'Update'}</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{post.content}</p>
          </div>
          <div className="flex items-center gap-6 px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
            <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-500 transition-colors">
              <Heart className="w-4 h-4" /> {post.likes}
            </button>
            <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-500 transition-colors">
              <MessageCircle className="w-4 h-4" /> {post.comments}
            </button>
            <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-500 transition-colors ml-auto">
              <Send className="w-4 h-4" /> Share
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Bidding Marketplace Preview
const BiddingPreview = () => (
  <div className="max-w-lg mx-auto" data-testid="bidding-preview">
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-5 border-b border-amber-100">
        <div className="flex items-center gap-3 mb-3">
          <img src="https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=60&h=60&fit=crop&crop=face" alt="" className="w-14 h-14 rounded-full object-cover border-2 border-amber-200" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-bold">Sarah Johnson</p>
              <span className="bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <Star className="w-2.5 h-2.5 fill-white" /> TOP PRO
              </span>
            </div>
            <p className="text-xs text-gray-600">Real Estate Agent | Premier Realty</p>
            <p className="text-xs text-gray-500">2,400 emails/month | Troy, MI</p>
          </div>
        </div>
        <div className="flex gap-2">
          {[1,2,3,4,5].map(i => (
            <div key={i} className={`flex-1 h-2 rounded-full ${i <= 5 ? 'bg-red-400' : 'bg-gray-200'}`} />
          ))}
        </div>
        <p className="text-[11px] text-red-600 font-medium mt-1.5">All 5 slots filled — high demand!</p>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold">Current bid: $35/mo</p>
            <p className="text-xs text-gray-500">3 pros on the waiting list</p>
          </div>
          <div className="flex items-center gap-1 text-amber-600">
            <Gavel className="w-4 h-4" />
            <span className="text-xs font-bold">BIDDING OPEN</span>
          </div>
        </div>
        <div className="space-y-2 mb-4">
          {[
            { name: 'David Kim', bid: '$35/mo', time: '2h ago' },
            { name: 'James Wilson', bid: '$30/mo', time: '5h ago' },
            { name: 'Lisa Thompson', bid: '$25/mo', time: '1d ago' },
          ].map((bid, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
              <span className="font-medium">{bid.name}</span>
              <div className="flex items-center gap-3">
                <span className="font-bold text-green-600">{bid.bid}</span>
                <span className="text-gray-400">{bid.time}</span>
              </div>
            </div>
          ))}
        </div>
        <button className="w-full bg-amber-500 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-amber-600 transition-colors">
          Place Your Bid
        </button>
      </div>
    </div>
  </div>
);

// Testimonial Card
const TestimonialCard = ({ name, role, company, quote, photo }) => (
  <Card className="hover:shadow-lg transition-shadow" data-testid={`testimonial-${name.replace(' ', '-').toLowerCase()}`}>
    <CardContent className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <img src={photo} alt={name} className="w-12 h-12 rounded-full object-cover" />
        <div>
          <p className="font-semibold">{name}</p>
          <p className="text-xs text-muted-foreground">{role} at {company}</p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground italic">"{quote}"</p>
      <div className="flex gap-0.5 mt-3">
        {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
      </div>
    </CardContent>
  </Card>
);

export const LandingPage = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [promoVideoUrl, setPromoVideoUrl] = useState(null);
  const [platformStats, setPlatformStats] = useState({ total_users: 0, max_founding: 1000 });
  const [activeTutorialVideo, setActiveTutorialVideo] = useState(null);
  const howItWorksRef = useRef(null);

  // Public tutorial videos shown in the "Watch Demo" dropdown at the top of
  // the landing page. Add new entries here as more MP4s are uploaded to
  // the GridFS `tutorial_videos` bucket.
  const publicTutorialVideos = [
    {
      key: 'signatures-create',
      title: 'How to Create a Signature',
      description: 'Watch how a professional builds a polished email signature in minutes with ConnectMail.',
      duration: '42s',
      video_url: '/api/tutorials/video/tutorial-signatures-v1',
    },
    {
      key: 'groups-create',
      title: 'How to Create a Group',
      description: 'See how professionals set up a local ConnectMail group in under a minute.',
      duration: '45s',
      video_url: '/api/tutorials/video/tutorial-groups-v1',
    },
  ];

  const spotsRemaining = Math.max(0, platformStats.max_founding - platformStats.total_users);

  const DEMO_STEPS = [
    { title: 'Create Your Profile', description: 'Sign up free and build your professional profile. Tell us your profession, location, and business — our AI does the rest.', icon: UserPlus, color: '#22c55e' },
    { title: 'AI Builds Your Signature', description: 'Our AI generates a stunning, professional email signature tailored to your industry. Includes your photo, social links, and ad slots.', icon: Sparkles, color: '#3b82f6' },
    { title: 'Get Matched With Pros', description: 'Our smart matching connects you with complementary professionals. A realtor meets a mortgage broker. A lawyer meets an accountant.', icon: Target, color: '#f59e0b' },
    { title: 'Sell Ad Space & Earn', description: 'Matched pros place ads on your signature for $20/mo. You keep 50% commission on every ad. More connections = more revenue.', icon: DollarSign, color: '#10b981' },
    { title: 'Scale Your Network', description: 'Join groups, attend events, import contacts, and let the AI Coach help you grow. Every email you send works for you.', icon: TrendingUp, color: '#8b5cf6' },
  ];

  useEffect(() => {
    // Check if a promo video exists
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promo-video-url`)
      .then(r => r.json())
      .then(d => { if (d.exists && d.published) setPromoVideoUrl(d.video_url); })
      .catch(() => {});
    // Fetch platform stats for founding spots counter
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/stats`)
      .then(r => r.json())
      .then(d => setPlatformStats(d))
      .catch(() => {});
    // Funnel: landing page viewed + dwell-time tracking
    logFunnel('landing_viewed');
    const stopDwell = trackDwellTime('landing_dwell');
    return () => { stopDwell(); };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex(prev => (prev + 1) % ROTATING_WORDS.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!showDemoModal) { setDemoStep(0); return; }
    const timer = setInterval(() => {
      setDemoStep(prev => (prev + 1) % DEMO_STEPS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [showDemoModal]);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1e3a5f] border-b border-[#2d4a6f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2" data-testid="landing-header-logo">
              <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-8 h-8 rounded-md bg-white p-0.5" />
              <span className="font-semibold text-lg tracking-tight font-['Outfit'] text-white">ConnectMail</span>
            </div>
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white/70 hover:text-white hover:bg-white/10 gap-1.5 hidden sm:flex"
                    data-testid="watch-demo-btn"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Watch Demo
                    <ChevronDown className="w-3 h-3 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80" data-testid="watch-demo-menu">
                  <DropdownMenuLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    See how it works
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {publicTutorialVideos.map((v) => (
                    <DropdownMenuItem
                      key={v.key}
                      onClick={() => {
                        setActiveTutorialVideo(v);
                        logFunnel('hero_cta_clicked', { location: 'watch-demo-dropdown', video: v.key });
                      }}
                      className="cursor-pointer flex items-start gap-3 py-2.5"
                      data-testid={`watch-demo-item-${v.key}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shrink-0">
                        <Play className="w-4 h-4 text-white fill-current" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate">{v.title}</div>
                        <div className="text-[11px] text-slate-500 leading-snug line-clamp-2">{v.description}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{v.duration}</div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  {publicTutorialVideos.length === 0 && (
                    <div className="px-3 py-4 text-xs text-slate-500 text-center">More demos coming soon</div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/login">
                <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="login-link">Log In</Button>
              </Link>
              <Link to="/register" onClick={() => logFunnel('hero_cta_clicked', { location: 'header' })}>
                <Button className="bg-white text-[#1e3a5f] hover:bg-white/90" data-testid="get-started-btn">Start Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-28 px-4 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a5f]/5 via-transparent to-green-500/5" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-800 text-sm font-semibold mb-6 animate-pulse">
              <Award className="w-4 h-4" />
              <span>Free for the first 1,000 members — {spotsRemaining > 0 ? `${spotsRemaining} spots remaining` : 'Almost full!'}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight font-['Outfit'] text-foreground mb-6">
              Generate More Referrals
              <br />
              From the Emails{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1e3a5f] via-blue-500 to-green-500 transition-all duration-500">
                You're Already Sending.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              ConnectMail helps professionals build trusted referral partnerships, increase their visibility,
              promote one another, and stay in front of clients through professionally designed email signatures
              and business networking.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" onClick={() => logFunnel('hero_cta_clicked', { location: 'hero' })}>
                <Button size="lg" className="w-full sm:w-auto text-lg px-8 group" data-testid="hero-cta">
                  Claim Your Founding Member Spot
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="w-full sm:w-auto" onClick={scrollToHowItWorks} data-testid="how-it-works-btn">
                <ChevronDown className="w-4 h-4 mr-2" />
                Watch How It Works
              </Button>
            </div>
            <div className="flex items-center justify-center gap-8 mt-8 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>100% free to join</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>All features included</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Earn 50% commission</span>
              </div>
            </div>
          </div>

          {/* Founding Member offer card — reassures visitors there's zero financial risk */}
          <div className="mt-10 max-w-2xl mx-auto" data-testid="founding-offer-card">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-300 shadow-lg shadow-amber-200/40 p-5 sm:p-6">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-amber-200/30 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-yellow-200/30 rounded-full blur-2xl pointer-events-none" />

              <div className="relative flex flex-col sm:flex-row items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg ring-2 ring-amber-200">
                  <Trophy className="w-8 h-8 text-white" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">Founding Member Offer</span>
                    <span className="px-2 py-0.5 rounded-full bg-amber-600 text-white text-[10px] font-bold">LIMITED · 815 SPOTS LEFT</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-amber-900 font-['Outfit'] mb-3 leading-tight">
                    Join now and lock in premium access for life — at zero cost.
                  </h3>

                  <ul className="space-y-2 mb-4">
                    {[
                      <>First <strong>1,000 members</strong> receive Founding Member status</>,
                      <>Premium membership included <strong>at no cost</strong></>,
                      <>One free <strong>advertising placement</strong> included</>,
                      <><strong>No credit card</strong> required</>,
                      <>Keep your Founding Member status <strong>permanently</strong></>,
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                        <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/register" className="inline-block">
                    <Button
                      className="bg-amber-600 hover:bg-amber-700 text-white font-bold shadow-md"
                      data-testid="founding-offer-cta"
                    >
                      Claim My Founding Member Spot
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Pro Hero Cards */}
          <div className="mt-16">
            <p className="text-center text-sm text-muted-foreground mb-4">AI-matched professionals ready to cross-promote with you</p>
            <ProHeroCards />
          </div>
        </div>
      </section>

      {/* Network Flow Visual — shows how professionals refer each other */}
      <section className="py-12 px-4 bg-white" data-testid="network-flow-section">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-xs uppercase tracking-[0.18em] font-semibold text-primary mb-6">
            How Professionals Connect on ConnectMail
          </p>
          <div className="flex flex-col items-center gap-2 sm:gap-3" data-testid="network-flow-chain">
            {[
              { label: 'Realtor', color: 'bg-blue-500' },
              { label: 'Loan Officer', color: 'bg-emerald-500' },
              { label: 'Insurance Agent', color: 'bg-amber-500' },
              { label: 'Attorney', color: 'bg-violet-500' },
              { label: 'Accountant', color: 'bg-rose-500' },
            ].map((node, i, arr) => (
              <div key={node.label} className="flex flex-col items-center">
                <div className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-white border border-border shadow-sm hover:shadow-md transition-shadow">
                  <span className={`w-2.5 h-2.5 rounded-full ${node.color}`} />
                  <span className="text-sm sm:text-base font-semibold text-foreground">{node.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <ChevronDown className="w-4 h-4 text-muted-foreground my-0.5" />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6 max-w-xl mx-auto">
            Every professional strengthens the network by referring trusted partners and staying visible
            through every email they send.
          </p>
        </div>
      </section>

      {/* Why ConnectMail? — 3 benefit cards */}
      <section className="py-16 px-4 bg-gradient-to-b from-white via-emerald-50/20 to-white" data-testid="why-connectmail-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Why ConnectMail?
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-3">
              Built to grow your business — not just your contact list.
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Three simple outcomes ConnectMail delivers from day one.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: TrendingUp,
                color: 'from-emerald-500 to-teal-600',
                bg: 'bg-emerald-50',
                accent: 'text-emerald-600',
                title: 'Generate More Referrals',
                description: 'Build trusted relationships with professionals who complement your business.',
              },
              {
                icon: Mail,
                color: 'from-blue-500 to-indigo-600',
                bg: 'bg-blue-50',
                accent: 'text-blue-600',
                title: 'Increase Your Exposure',
                description: 'Keep your name in front of customers every time you send an email.',
              },
              {
                icon: Users,
                color: 'from-amber-500 to-orange-600',
                bg: 'bg-amber-50',
                accent: 'text-amber-600',
                title: 'Grow Together',
                description: 'Connect, collaborate, host events, and promote one another through one professional platform.',
              },
            ].map((b) => (
              <div
                key={b.title}
                className="bg-white border border-border/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`why-card-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-12 h-12 ${b.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <b.icon className={`w-6 h-6 ${b.accent}`} />
                </div>
                <h3 className="text-lg font-bold mb-2 font-['Outfit']">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8 font-semibold tracking-wide">
            Connect. Promote. Grow.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section ref={howItWorksRef} className="py-20 bg-muted/30" data-testid="how-it-works-section" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-5">
              <Zap className="w-4 h-4" />
              Simple Process
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-4">
              How ConnectMail Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From signing up to earning passive income — here's the journey
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {HOW_IT_WORKS.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={item.step} className="relative">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className={`w-16 h-16 ${item.color} text-white rounded-2xl flex items-center justify-center shadow-lg`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center border-2 border-border -mt-2 relative z-10">
                      <span className="text-sm font-bold">{item.step}</span>
                    </div>
                    <h3 className="font-semibold text-lg">{item.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                  </div>
                  {index < HOW_IT_WORKS.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-border" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Email Promotion Showcase */}
      <section className="py-20" data-testid="email-promo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <EmailPromoMockup />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
                <DollarSign className="w-4 h-4" />
                Your Emails = Revenue
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-6">
                Stay Visible Every Time You Send an Email
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Your AI-generated email signature includes ad slots where other pros can promote themselves.
                Every time someone clicks — you earn 50%. It's passive income from emails you already send.
              </p>
              <ul className="space-y-4">
                {[
                  { icon: Sparkles, text: 'Choose from multiple AI-tailored signature designs' },
                  { icon: Users, text: 'Up to 5 ad slots on your signature for other pros' },
                  { icon: DollarSign, text: 'Earn 50% commission on every filled slot' },
                  { icon: BarChart3, text: 'Real-time analytics on clicks and earnings' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-muted-foreground">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/demo">
              <Button variant="outline" size="lg" className="gap-2" data-testid="full-demo-link">
                <Play className="w-4 h-4" />
                See Full Interactive Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Is ConnectMail Right for You? — profession audience grid */}
      <section className="py-20 bg-white" data-testid="audience-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Users className="w-4 h-4" />
              Is ConnectMail Right for You?
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-3">
              Built for Professionals Who Grow Through Referrals
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
              Whether you're building relationships locally or expanding your professional network nationwide,
              ConnectMail is designed for professionals who rely on trust, visibility, and referrals.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[
              { name: 'Realtors', icon: Home, color: 'text-blue-600', bg: 'bg-blue-50' },
              { name: 'Insurance Agents', icon: Shield, color: 'text-amber-600', bg: 'bg-amber-50' },
              { name: 'Loan Officers', icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { name: 'Attorneys', icon: Gavel, color: 'text-violet-600', bg: 'bg-violet-50' },
              { name: 'Accountants', icon: Calculator, color: 'text-rose-600', bg: 'bg-rose-50' },
              { name: 'Financial Advisors', icon: TrendingUp, color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { name: 'Interior Designers', icon: Palette, color: 'text-pink-600', bg: 'bg-pink-50' },
              { name: 'Business Owners', icon: Briefcase, color: 'text-slate-700', bg: 'bg-slate-100' },
              { name: 'Marketing Professionals', icon: Megaphone, color: 'text-orange-600', bg: 'bg-orange-50' },
              { name: 'Mortgage Brokers', icon: Building2, color: 'text-cyan-600', bg: 'bg-cyan-50' },
              { name: 'Title Companies', icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' },
              { name: 'Contractors', icon: Hammer, color: 'text-amber-700', bg: 'bg-amber-50' },
              { name: 'Home Inspectors', icon: Search, color: 'text-emerald-700', bg: 'bg-emerald-50' },
              { name: 'Commercial Professionals', icon: Building, color: 'text-blue-700', bg: 'bg-blue-50' },
            ].map((p) => (
              <div
                key={p.name}
                className="bg-white border border-border/60 rounded-xl p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-3"
                data-testid={`audience-card-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-10 h-10 ${p.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <p.icon className={`w-5 h-5 ${p.color}`} />
                </div>
                <span className="text-sm font-semibold text-slate-800 leading-tight">{p.name}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-2xl mx-auto text-center bg-gradient-to-br from-emerald-50/60 to-emerald-50/20 border border-emerald-200/60 rounded-xl px-5 py-4">
            <p className="text-sm font-bold text-emerald-900 mb-1">Don't see your profession?</p>
            <p className="text-sm text-muted-foreground">
              ConnectMail is built for any professional who wants to build stronger business relationships and generate more referrals.
            </p>
          </div>
        </div>
      </section>

      {/* Bidding & Premium Marketplace */}
      <section className="py-20 bg-gradient-to-b from-amber-50/50 to-background" data-testid="bidding-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-6">
                <Gavel className="w-4 h-4" />
                Premium Marketplace
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-6">
                Promote Your Business to Trusted Professionals
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                When all 6 of your ad slots are booked and there's a waiting list, you can open bidding.
                Pros compete for your high-traffic signature space — and you set the price.
              </p>
              <ul className="space-y-4">
                {[
                  { icon: Star, text: 'All slots full? Pros join your waiting list automatically' },
                  { icon: Gavel, text: 'Open bidding for premium slot pricing when demand is high' },
                  { icon: TrendingUp, text: 'High-demand pros can charge $30, $40, $50+ per slot' },
                  { icon: Bell, text: 'Get notified when someone bids on your signature space' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-muted-foreground">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <BiddingPreview />
          </div>
        </div>
      </section>

      {/* Success Stories — marketing videos (hero + 4 supporting) */}
      <SuccessStoriesSection />

      {/* Founding Member Access */}
      <section className="py-20" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold mb-5">
              <Award className="w-4 h-4" />
              Founding Member Program
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-4">
              Everything Free. For Real.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The first 1,000 members are <strong>automatically</strong> granted Founding Member status with full premium access — for life. No invites required, no subscription, no downgrade.
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-14">
            {/* Single Plan Card */}
            <Card className="border-2 border-emerald-500/30 shadow-xl rounded-2xl overflow-hidden relative" data-testid="plan-founding">
              <div className="absolute -top-0 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-b-lg">EARLY ACCESS</div>
              <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5f8a] px-8 py-6 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold font-['Outfit'] text-white">All Features Included</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-4xl font-bold text-emerald-400">$0</span>
                      <span className="text-white/60 text-sm">/month for first 1,000 members</span>
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-white/80 text-sm font-medium">{spotsRemaining > 0 ? `${spotsRemaining} spots left` : 'Almost full!'}</p>
                    <div className="w-32 h-2 bg-white/20 rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${Math.min(100, (platformStats.total_users / 1000) * 100)}%` }} />
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm mb-8">
                  {[
                    'AI-generated email signatures',
                    'Download, copy & install signatures',
                    'AI Networking Coach (10/day)',
                    'Create groups & host events',
                    'Up to 6 ad slots on your signature',
                    'Earn 50% commission per filled slot',
                    'Marketplace ad slot placement',
                    'Ad performance analytics & insights',
                    'Smart matching & icebreakers',
                    'QR-code digital business card',
                    'Premium bidding marketplace',
                    'Contact import & referral tools',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Trophy className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Automatic Founding Member status</p>
                      <p className="text-xs text-amber-700 mt-1">Sign up while there are still seats open and you're <strong>instantly</strong> a Founding Member. The <strong>"Founding Member"</strong> badge appears on your profile and signatures — premium access stays free for life, even after paid plans launch.</p>
                    </div>
                  </div>
                </div>

                <Link to="/register">
                  <Button size="lg" className="w-full text-base group bg-emerald-600 hover:bg-emerald-700" data-testid="plan-founding-cta">
                    Join Free — {spotsRemaining > 0 ? `${spotsRemaining} Spots Left` : 'Limited Spots'}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <p className="text-xs text-muted-foreground mt-3 text-center">No credit card required. No hidden fees. Cancel anytime.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Feed Preview */}
      <section className="py-20 bg-muted/30" data-testid="feed-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <MessageCircle className="w-4 h-4" />
                Community Feed
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-6">
                A Feed That Grows Your Business
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Share updates, promote your services, post pro tips, and engage with your network.
                Every interaction strengthens your connections — and leads to more referrals.
              </p>
              <ul className="space-y-4">
                {[
                  { icon: Heart, text: 'Like, comment, and share posts from your network' },
                  { icon: TrendingUp, text: 'Promote your business with targeted updates' },
                  { icon: Calendar, text: 'Announce events and find networking opportunities' },
                  { icon: Award, text: 'Build your reputation as a trusted professional' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-muted-foreground">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <SocialFeedPreview />
          </div>
        </div>
      </section>

      {/* AI Coach Section */}
      <section className="py-20" data-testid="ai-coach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
                <Bot className="w-4 h-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-6">
                Discover Better Referral Opportunities
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our AI coach analyzes your profile and business goals to suggest the perfect connections.
                Get personalized icebreakers, networking tips, and message templates.
              </p>
              <ul className="space-y-4">
                {[
                  'Smart connection recommendations based on your target market',
                  'AI-generated icebreakers for each professional',
                  'Profile optimization tips to attract more connections',
                  'Message templates that get responses'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <AICoachPreview />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-muted/30" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-4">
              Loved by Professionals
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard name="Sarah Miller" role="Real Estate Agent" company="Premier Realty" quote="The AI coach suggested I connect with a mortgage broker nearby. We've exchanged 12 referrals in 3 months!" photo="https://images.unsplash.com/photo-1765005204058-10418f5123c5?w=100&h=100&fit=crop&crop=face" />
            <TestimonialCard name="Michael Chen" role="Mortgage Broker" company="HomeFirst Finance" quote="Smart matching found me the perfect real estate partners. My close rate is up 40% from referrals alone." photo="https://images.unsplash.com/photo-1659353221237-6a1cfb73fd90?w=100&h=100&fit=crop&crop=face" />
            <TestimonialCard name="Emily Rodriguez" role="Insurance Agent" company="SecureLife" quote="My signature earns me $50/month in commissions. Passive income from emails I was already sending!" photo="https://images.unsplash.com/photo-1758598306913-5cd682b9e53b?w=100&h=100&fit=crop&crop=face" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5f8a]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-['Outfit'] mb-6 text-white">
            Ready to Turn Your Network Into Revenue?
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            Join thousands of professionals who are networking smarter and earning passive income through their everyday emails.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="text-lg px-8 bg-white text-[#1e3a5f] hover:bg-white/90" data-testid="final-cta">
                Create Free Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="text-lg px-8 border-white/30 text-white hover:bg-white/10" onClick={scrollToHowItWorks}>
              See How It Works
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2" data-testid="landing-footer-logo">
              <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-6 h-6 rounded bg-primary p-0.5" />
              <span className="font-medium text-sm">ConnectMail</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} ConnectMail. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowDemoModal(false)} data-testid="demo-modal-overlay">
          <div className="relative w-full max-w-2xl mx-4 bg-[#0f1729] rounded-2xl overflow-hidden shadow-2xl border border-white/10" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-6 h-6 rounded bg-white p-0.5" />
                <span className="text-white font-semibold font-['Outfit']">ConnectMail Demo</span>
              </div>
              <button onClick={() => setShowDemoModal(false)} className="text-white/50 hover:text-white transition-colors text-xl leading-none" data-testid="demo-modal-close">&times;</button>
            </div>

            {/* Demo Content — Video if available, otherwise animated walkthrough */}
            {promoVideoUrl ? (
              <div className="p-4">
                <video
                  src={`${process.env.REACT_APP_BACKEND_URL}${promoVideoUrl}`}
                  controls
                  autoPlay
                  className="w-full rounded-lg"
                  data-testid="demo-video-player"
                />
                <div className="flex justify-center pt-4">
                  <Link to="/register">
                    <Button className="bg-green-500 hover:bg-green-600 text-white" data-testid="demo-signup-btn">
                      Start Free <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
            <>
            {/* Animated Demo Content */}
            <div className="p-8 min-h-[360px] flex flex-col items-center justify-center text-center relative">
              {/* Step indicator */}
              <div className="flex gap-2 mb-8">
                {DEMO_STEPS.map((_, i) => (
                  <button key={i} onClick={() => setDemoStep(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === demoStep ? 'bg-white scale-125' : 'bg-white/25 hover:bg-white/40'}`} data-testid={`demo-step-dot-${i}`} />
                ))}
              </div>

              {/* Animated icon */}
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500" style={{ background: `${DEMO_STEPS[demoStep].color}20`, boxShadow: `0 0 40px ${DEMO_STEPS[demoStep].color}30` }}>
                {(() => { const Icon = DEMO_STEPS[demoStep].icon; return <Icon className="w-10 h-10 transition-all duration-500" style={{ color: DEMO_STEPS[demoStep].color }} />; })()}
              </div>

              {/* Step title */}
              <div className="text-xs font-medium uppercase tracking-widest mb-2 transition-all duration-500" style={{ color: DEMO_STEPS[demoStep].color }}>
                Step {demoStep + 1} of {DEMO_STEPS.length}
              </div>
              <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-3 transition-all duration-300">
                {DEMO_STEPS[demoStep].title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-md">
                {DEMO_STEPS[demoStep].description}
              </p>

              {/* Progress bar */}
              <div className="w-full max-w-xs mt-8 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-300" style={{ width: `${((demoStep + 1) / DEMO_STEPS.length) * 100}%`, background: DEMO_STEPS[demoStep].color }} />
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => setDemoStep(prev => prev > 0 ? prev - 1 : DEMO_STEPS.length - 1)} data-testid="demo-prev-btn">
                Previous
              </Button>
              {demoStep === DEMO_STEPS.length - 1 ? (
                <Link to="/register">
                  <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" data-testid="demo-signup-btn">
                    Start Free <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              ) : (
                <Button size="sm" className="bg-white/10 hover:bg-white/20 text-white" onClick={() => setDemoStep(prev => prev + 1)} data-testid="demo-next-btn">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
            </>
            )}
          </div>
        </div>
      )}
      <LandingChatBot />
      <ExitIntentSurvey />
      {activeTutorialVideo && (
        <TutorialVideoModal
          tutorial={activeTutorialVideo}
          onClose={() => setActiveTutorialVideo(null)}
        />
      )}
    </div>
  );
};

export default LandingPage;
